import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Box,
  HStack,
  Stack,
  Button,
  useToast 
} from '@chakra-ui/react';
import { getArchiveData, updateArchiveData } from '../../../services/Archive';
import Form from '@rjsf/chakra-ui';
import validator from "@rjsf/validator-ajv8";
import FormLoader from '../../../CommonComponents/FormLoader';
import { useSelector } from 'react-redux';
import { handleApiError, showSuccessToast } from '../../../util/errorHandler';

const Section6 = () => {

  const { alias } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [quickSave, setQuickSave] = useState(false);
  const [leftFormData, setLeftFormData] = useState({});
  const [rightFormData, setRightFormData] = useState({});
  const [combinedFormData, setCombinedFormData] = useState({});
  const previewEnabled = useSelector(state => state.preview.isEnabled);
  const toast = useToast();

  // Split data for two-column layout
  const splitFormData = (data = {}) => {
    const leftFields = ['_id', 'le_inr', 'he_inr', 'le_usd', 'he_usd', 'le_gbp', 'he_gbp'];
    const rightFields = ['sp_inr', 'sp_usd', 'sp_gbp', 'usd_rate', 'gbp_rate', 'euro_rate'];
    
    const leftData = {};
    const rightData = {};
    
    leftFields.forEach(field => {
      if (data.hasOwnProperty(field)) {
        leftData[field] = data[field];
      }
    });
    
    rightFields.forEach(field => {
      if (data.hasOwnProperty(field)) {
        rightData[field] = data[field];
      }
    });
    
    return { leftData, rightData };
  };
  
  // Combine data from two forms
  const combineFormData = (leftData, rightData) => {
    return { ...leftData, ...rightData };
  };

  // Form change handlers
  const handleLeftFormChange = (data) => {
    setLeftFormData(data.formData);
  };

  const handleRightFormChange = (data) => {
    setRightFormData(data.formData);
  };

  const handleCombinedFormChange = (data) => {
    setCombinedFormData(data.formData);
  };

  // Left column schema
  const leftColumnSchema = {
    "title": "",
    "type": "object",
    "properties": {
      "_id": { "type": "string" },
      "le_inr": {
        "type": ["string", "object"],
        "title": "Lower Estimate (INR)"
      },
      "he_inr": {
        "type": ["string", "object"],
        "title": "Higher Estimate (INR)"
      },
      "le_usd": {
        "type": ["string", "object"],
        "title": "Lower Estimate (USD)"
      },
      "he_usd": {
        "type": ["string", "object"],
        "title": "Higher Estimate (USD)"
      },
      "le_gbp": {
        "type": ["string", "object"],
        "title": "Lower Estimate (GBP)"
      },
      "he_gbp": {
        "type": ["string", "object"],
        "title": "Higher Estimate (GBP)"
      }
    }
  };

  // Right column schema
  const rightColumnSchema = {
    "title": "",
    "type": "object",
    "properties": {
      "sp_inr": {
        "type": ["string", "object"],
        "title": "Sold Price (INR)"
      },
      "sp_usd": {
        "type": ["string", "object"],
        "title": "Sold Price (USD)"
      },
      "sp_gbp": {
        "type": ["string", "object"],
        "title": "Sold Price (GBP)"
      },
      "usd_rate": {
        "type": ["string", "object"],
        "title": "USD Rate"
      },
      "gbp_rate": {
        "type": ["string", "object"],
        "title": "GBP Rate"
      },
      "euro_rate": {
        "type": ["string", "object"],
        "title": "Euro Rate"
      }
    }
  };

  // Combined schema for preview mode
  const combinedSchema = {
    "title": "Quadrant-6 fields",
    "description": "",
    "type": "object",
    "properties": {
      "_id": { "type": "string" },
      "le_inr": {
        "type": ["string", "object"],
        "title": "Lower Estimate (INR)"
      },
      "he_inr": {
        "type": ["string", "object"],
        "title": "Higher Estimate (INR)"
      },
      "le_usd": {
        "type": ["string", "object"],
        "title": "Lower Estimate (USD)"
      },
      "he_usd": {
        "type": ["string", "object"],
        "title": "Higher Estimate (USD)"
      },
      "le_gbp": {
        "type": ["string", "object"],
        "title": "Lower Estimate (GBP)"
      },
      "he_gbp": {
        "type": ["string", "object"],
        "title": "Higher Estimate (GBP)"
      },
      "sp_inr": {
        "type": ["string", "object"],
        "title": "Sold Price (INR)"
      },
      "sp_usd": {
        "type": ["string", "object"],
        "title": "Sold Price (USD)"
      },
      "sp_gbp": {
        "type": ["string", "object"],
        "title": "Sold Price (GBP)"
      },
      "usd_rate": {
        "type": ["string", "object"],
        "title": "USD Rate"
      },
      "gbp_rate": {
        "type": ["string", "object"],
        "title": "GBP Rate"
      },
      "euro_rate": {
        "type": ["string", "object"],
        "title": "Euro Rate"
      }
    }
  };

  // Left column UI schema
  const leftColumnUiSchema = {
    "ui:submitButtonOptions": { "norender": true },
    "_id": { "ui:widget": "hidden" },
    "ui:style": {
      fontSize: "14px",
    },
    "le_inr": { 
      "ui:emptyValue": "",
      "ui:widget": "text",
      "ui:options": {
        inputType: "text"
      }
    },
    "he_inr": { 
      "ui:emptyValue": "",
      "ui:widget": "text",
      "ui:options": {
        inputType: "text"
      }
    },
    "le_usd": { 
      "ui:emptyValue": "",
      "ui:widget": "text",
      "ui:options": {
        inputType: "text"
      }
    },
    "he_usd": { 
      "ui:emptyValue": "",
      "ui:widget": "text",
      "ui:options": {
        inputType: "text"
      }
    },
    "le_gbp": { 
      "ui:emptyValue": "",
      "ui:widget": "text",
      "ui:options": {
        inputType: "text"
      }
    },
    "he_gbp": { 
      "ui:emptyValue": "",
      "ui:widget": "text",
      "ui:options": {
        inputType: "text"
      }
    }
  };

  // Right column UI schema
  const rightColumnUiSchema = {
    "ui:submitButtonOptions": { "norender": true },
    "ui:style": {
      fontSize: "14px",
    },
    "sp_inr": { 
      "ui:emptyValue": "",
      "ui:widget": "text",
      "ui:options": {
        inputType: "text"
      }
    },
    "sp_usd": { 
      "ui:emptyValue": "",
      "ui:widget": "text",
      "ui:options": {
        inputType: "text"
      }
    },
    "sp_gbp": { 
      "ui:emptyValue": "",
      "ui:widget": "text",
      "ui:options": {
        inputType: "text"
      }
    },
    "usd_rate": { 
      "ui:emptyValue": "",
      "ui:widget": "text",
      "ui:options": {
        inputType: "text"
      }
    },
    "gbp_rate": { 
      "ui:emptyValue": "",
      "ui:widget": "text",
      "ui:options": {
        inputType: "text"
      }
    },
    "euro_rate": { 
      "ui:emptyValue": "",
      "ui:widget": "text",
      "ui:options": {
        inputType: "text"
      }
    }
  };

  // Combined UI schema for preview mode
  const combinedUiSchema = {
    "ui:submitButtonOptions": { "norender": true },
    "_id": { "ui:widget": "hidden" },
    "ui:style": {
      fontSize: "14px",
    },
    "le_inr": { 
      "ui:emptyValue": "",
      "ui:widget": "text",
      "ui:options": {
        inputType: "text"
      }
    },
    "he_inr": { 
      "ui:emptyValue": "",
      "ui:widget": "text",
      "ui:options": {
        inputType: "text"
      }
    },
    "le_usd": { 
      "ui:emptyValue": "",
      "ui:widget": "text",
      "ui:options": {
        inputType: "text"
      }
    },
    "he_usd": { 
      "ui:emptyValue": "",
      "ui:widget": "text",
      "ui:options": {
        inputType: "text"
      }
    },
    "le_gbp": { 
      "ui:emptyValue": "",
      "ui:widget": "text",
      "ui:options": {
        inputType: "text"
      }
    },
    "he_gbp": { 
      "ui:emptyValue": "",
      "ui:widget": "text",
      "ui:options": {
        inputType: "text"
      }
    },
    "sp_inr": { 
      "ui:emptyValue": "",
      "ui:widget": "text",
      "ui:options": {
        inputType: "text"
      }
    },
    "sp_usd": { 
      "ui:emptyValue": "",
      "ui:widget": "text",
      "ui:options": {
        inputType: "text"
      }
    },
    "sp_gbp": { 
      "ui:emptyValue": "",
      "ui:widget": "text",
      "ui:options": {
        inputType: "text"
      }
    },
    "usd_rate": { 
      "ui:emptyValue": "",
      "ui:widget": "text",
      "ui:options": {
        inputType: "text"
      }
    },
    "gbp_rate": { 
      "ui:emptyValue": "",
      "ui:widget": "text",
      "ui:options": {
        inputType: "text"
      }
    },
    "euro_rate": { 
      "ui:emptyValue": "",
      "ui:widget": "text",
      "ui:options": {
        inputType: "text"
      }
    }
  };

  // Process data to handle MongoDB decimal format
  const processDataForDisplay = (data) => {
    const stringFields = [
      'le_inr', 'he_inr', 'le_usd', 'he_usd', 
      'le_gbp', 'he_gbp', 'sp_inr', 'sp_usd', 'sp_gbp',
      'usd_rate', 'gbp_rate', 'euro_rate'
    ];
    
    const processedData = { ...data };
    
    stringFields.forEach(field => {
      if (processedData[field]) {
        // Handle MongoDB's $numberDecimal format
        if (typeof processedData[field] === 'object' && processedData[field].$numberDecimal) {
          processedData[field] = processedData[field].$numberDecimal;
        }
        // Handle other object formats
        else if (typeof processedData[field] === 'object') {
          processedData[field] = JSON.stringify(processedData[field]);
        }
      }
    });
    
    return processedData;
  };

  // Process data for submission
  const processDataForSubmission = (formData) => {
    const processedFormData = { ...formData };
    
    const stringFields = [
      'le_inr', 'he_inr', 'le_usd', 'he_usd', 
      'le_gbp', 'he_gbp', 'sp_inr', 'sp_usd', 'sp_gbp',
      'usd_rate', 'gbp_rate', 'euro_rate'
    ];
    
    stringFields.forEach(field => {
      const value = processedFormData[field];
      
      // Skip empty values
      if (!value) return;
      
      // If it's a valid number, convert to number
      if (!isNaN(parseFloat(value)) && isFinite(value)) {
        processedFormData[field] = parseFloat(value);
      }
      // If it looks like JSON, try to parse it
      else if (typeof value === 'string' && 
        (value.startsWith('{') || value.startsWith('['))) {
        try {
          const parsed = JSON.parse(value);
          // If it's a MongoDB decimal, extract the value
          if (parsed.$numberDecimal) {
            processedFormData[field] = parseFloat(parsed.$numberDecimal);
          } else {
            processedFormData[field] = parsed;
          }
        } catch (e) {
          // If parsing fails, keep it as a string
          console.warn(`Failed to parse ${field} as JSON:`, e);
        }
      }
    });

    return processedFormData;
  };

  // Fetch initial data
  useEffect(() => {
    const getBasicData = async () => {
      try {
        if (alias) {
          const result = await getArchiveData(alias, 'section-6');
          if (result) {
            const data = result?.data?.[0] || {};
            const processedData = processDataForDisplay(data);
            
            // Split data for two-column layout
            const { leftData, rightData } = splitFormData(processedData);
            setLeftFormData(leftData);
            setRightFormData(rightData);
            setCombinedFormData(processedData);
            setQuickSave(false);
          }
        } else {
          // For new records, initialize empty forms
          const emptyData = {};
          const { leftData, rightData } = splitFormData(emptyData);
          setLeftFormData(leftData);
          setRightFormData(rightData);
          setCombinedFormData(emptyData);
        }
      } catch (error) {
        handleApiError(error, toast, {
          defaultErrorMessage: "Failed to fetch data"
        });
      } finally {
        setIsLoading(false);
      }
    };

    getBasicData();
  }, [alias, toast, quickSave]);

  // Submit updated data
  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      let finalFormData;
      
      if (previewEnabled) {
        finalFormData = combinedFormData;
      } else {
        finalFormData = combineFormData(leftFormData, rightFormData);
      }
      
      // Remove _id from create operations
      if (!alias && finalFormData._id) {
        delete finalFormData._id;
      }

      // Process data for submission
      const processedFormData = processDataForSubmission(finalFormData);

      if (alias) {
        const result = await updateArchiveData(alias, processedFormData);

        if (result?.status === 200) {
          setQuickSave(prev => !prev);
          showSuccessToast(toast, "Data updated successfully");
        }
      }
    } catch (error) {
      handleApiError(error, toast, {
        defaultErrorMessage: "Failed to update data"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box maxW="auto" pb="100px">
      {isLoading ? (
        <FormLoader />
      ) : (
        <Box>
          {previewEnabled ? (
            /* Single column layout for preview mode */
            <Box display={"flex"} flexDirection={"row"} alignItems={"flex-start"}>
              <Box flex={1} bg="white" border="1px solid" borderColor="gray.200" p={4} borderRadius="md">
                <HStack justifyContent="space-between" mb={4}>
                  <Box fontSize="lg" fontWeight="bold">Archive Quadrant-6 Fields</Box>
                </HStack>
                <Form
                  schema={combinedSchema}
                  uiSchema={combinedUiSchema}
                  formData={combinedFormData}
                  validator={validator}
                  onChange={handleCombinedFormChange}
                  fontsize={"14px"}
                />
              </Box>
            </Box>
          ) : (
            /* Two column layout with separate forms */
            <Box display={"flex"} flexDirection={"row"} alignItems={"flex-start"}>
              <Stack flexDirection="row" gap={4} alignItems="flex-start" flex={1}>
                <HStack alignItems="flex-start" flex={1}>
                  {/* Left Column */}
                  <Box flex={1} bg="white" border="1px solid" borderColor="gray.200" p={4} borderRadius="md">
                    <HStack justifyContent="space-between" mb={4}>
                      <Box fontSize="lg" fontWeight="bold">Archive Quadrant-6 Fields</Box>
                    </HStack>
                    <Form
                      schema={leftColumnSchema}
                      uiSchema={leftColumnUiSchema}
                      formData={leftFormData}
                      validator={validator}
                      onChange={handleLeftFormChange}
                      fontsize={"14px"}
                    />
                  </Box>
                  {/* Right Column */}
                  <Box flex={1} bg="white" border="1px solid" borderColor="gray.200" p={4} borderRadius="md">
                    <Form
                      schema={rightColumnSchema}
                      uiSchema={rightColumnUiSchema}
                      formData={rightFormData}
                      validator={validator}
                      onChange={handleRightFormChange}
                      fontsize={"14px"}
                    />
                  </Box>
                </HStack>
              </Stack>
            </Box>
          )}
        </Box>
      )}
      
      {/* Fixed Save Button */}
      <Box
        position="fixed"
        bottom={0}
        left={"250px"}
        right={0}
        bg="white"
        borderTop="1px solid"
        borderColor="gray.200"
        p={4}
        zIndex={1000}
      >
        <Box maxW="auto" mx="auto" display="flex" justifyContent="center">
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            width="auto"
            margin="0 auto"
            textAlign="center"
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default Section6;